import { useEffect, useState } from 'react';
import { ITypedBaseResponse } from '@nkr/models/base-response';
import { IOpoCase } from '@nkr/models/opo-case';
import { AppActionCreators } from '@nkr/store/application';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import usePortalOpo from './use-portal-opo';
import { IOpoSwaps } from './use-opo-swaps';

export interface IOpoComplianceItem {
    key: string,
    label: string,
    count: number
}

interface IRetVal {
    pendingCaseCount: number | null,
    pendingSwapCount: number | null,
    complianceItems: IOpoComplianceItem[] | null,
    loading: boolean
}

const useOpoToDo = (): IRetVal => {
    const { dispatch } = useAppHookContext();
    const { opoId } = usePortalOpo();
    const [pendingCaseCount, setPendingCaseCount] = useState<number | null>(null);
    const [pendingSwapCount, setPendingSwapCount] = useState<number | null>(null);
    const [complianceItems, setComplianceItems] = useState<IOpoComplianceItem[] | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!opoId) {
            return;
        }

        let isCurrent = true;

        const getCaseCount = () => dispatch(AuthActionCreators.apiGet({ url: `${__API__}/opo/${opoId}/cases?past=false` }))
            .then((response: ITypedBaseResponse<IOpoCase[]>) => response && response.data.success ? response.data.results.length : null)
            .catch(() => null);

        const getSwapCount = () => dispatch(AuthActionCreators.apiGet({ url: `${__API__}/opo/${opoId}/pending-swaps` }))
            .then((response: ITypedBaseResponse<IOpoSwaps>) => response && response.data.success ? response.data.results.donorSwaps.length + response.data.results.remoteDonors.length : null)
            .catch(() => null);

        const getCompliance = () => dispatch(AuthActionCreators.apiGet({ url: `${__API__}/opo/${opoId}/compliance` }))
            .then((response: ITypedBaseResponse<IOpoComplianceItem[]>) => response && response.data.success ? response.data.results : null)
            .catch(() => null);

        const fetchData = async () => {
            setLoading(true);

            await dispatch(AppActionCreators.showLoadingAsync('Getting to-do items, one moment please...'));

            const [caseCount, swapCount, compliance] = await Promise.all([getCaseCount(), getSwapCount(), getCompliance()]);

            if (isCurrent) {
                setPendingCaseCount(caseCount);
                setPendingSwapCount(swapCount);
                setComplianceItems(compliance);
                setLoading(false);
                dispatch(AppActionCreators.hideLoading());
            }
        };

        fetchData();

        return () => {
            isCurrent = false;
            dispatch(AppActionCreators.hideLoading());
        };
    }, [opoId]);

    return { pendingCaseCount, pendingSwapCount, complianceItems, loading };
}

export default useOpoToDo;
